import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ChevronLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export default function Checkout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [selected, setSelected] = useState("general");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.paid) navigate("/watch", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/event/pricing").then((r) => setTiers(r.data.tiers || []));
  }, []);

  const payWithStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/create-session", {
        origin_url: window.location.origin,
        tier_id: selected,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to start checkout");
      setBusy(false);
    }
  };

  if (!tiers.length) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const selectedTier = tiers.find((t) => t.id === selected) || tiers[0];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="checkout-back-link" className="flex items-center gap-3 hover:opacity-80">
            <ChevronLeft size={16} className="text-zinc-400" />
            <Logo size={28} />
          </Link>
          <button onClick={logout} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="checkout-logout-btn">
            Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="max-w-2xl">
          <p className="eyebrow text-rose-500">Choose your tier</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 uppercase leading-none">
            Step into the<br />Corporate Clash
          </h1>
          <p className="mt-4 text-zinc-400">
            Every tier unlocks the same live stream. Choose the amount you want
            to give — every extra dollar goes straight to the cause.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6 mt-12" data-testid="tier-grid">
          <div className="lg:col-span-3 grid sm:grid-cols-3 gap-px bg-white/10">
            {tiers.map((t) => {
              const active = t.id === selected;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className={`text-left p-8 transition-colors flex flex-col ${
                    active ? "bg-rose-600 text-white" : "bg-[#0a0a0a] hover:bg-[#121212]"
                  }`}
                  data-testid={`tier-card-${t.id}`}
                >
                  <div className="flex items-start justify-between">
                    <p className={`eyebrow ${active ? "text-white/80" : "text-zinc-500"}`}>{t.label}</p>
                    <div
                      className={`w-5 h-5 border-2 ${
                        active ? "bg-white border-white" : "border-white/30"
                      } grid place-items-center`}
                    >
                      {active && <CheckCircle2 size={14} className="text-rose-600" />}
                    </div>
                  </div>
                  <p className="font-display text-7xl mt-6 leading-none">${Math.round(t.amount)}</p>
                  <p className={`text-sm mt-4 ${active ? "text-white/80" : "text-zinc-400"}`}>
                    {t.tagline}
                  </p>
                </button>
              );
            })}
          </div>

          <aside className="lg:col-span-1">
            <div className="card-flat p-6 sticky top-6" data-testid="order-summary">
              <p className="eyebrow text-zinc-500">Order Summary</p>
              <div className="mt-6 flex justify-between text-sm">
                <span className="text-zinc-400">{selectedTier.label}</span>
                <span>${selectedTier.amount.toFixed(2)}</span>
              </div>
              <div className="my-5 border-t border-white/10" />
              <div className="flex justify-between items-baseline">
                <span className="eyebrow text-zinc-400">Total</span>
                <span className="font-display text-4xl">
                  ${selectedTier.amount.toFixed(0)}
                </span>
              </div>
              <button
                onClick={payWithStripe}
                disabled={busy}
                className="btn-primary w-full py-4 text-xs mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="stripe-pay-btn"
              >
                {busy && <Loader2 className="animate-spin" size={16} />}
                Pay ${selectedTier.amount.toFixed(0)} with Stripe
              </button>
              <div className="mt-6 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
                <ShieldCheck size={14} className="text-rose-500 shrink-0 mt-0.5" />
                <span>
                  Buying as <strong className="text-zinc-300">{user?.email}</strong>. Locked to one
                  device at a time and watermarked with your email.
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
